import PropTypes from 'prop-types'
import {
  Lightbulb,
  Filter,
  BarChart3,
  AlertCircle,
  HelpCircle,
  Copy,
  Check,
} from 'lucide-react'
import { useState } from 'react'

/**
 * AiTextResult Component
 *
 * Displays the text response with appropriate icon, badge, and metadata
 */
export default function AiTextResult({
  type,
  text,
  cached,
  cacheKey,
  metadata,
  suggestions,
  insights,
}) {
  const [copied, setCopied] = useState(false)

  const handleCopyShareLink = () => {
    if (cacheKey) {
      const url = `${window.location.origin}/ai-dashboard?cache=${cacheKey}`
      // eslint-disable-next-line no-undef
      navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const getTypeIcon = () => {
    switch (type) {
      case 'answer':
        return <Lightbulb className="w-5 h-5 text-blue-600" />
      case 'filter':
        return <Filter className="w-5 h-5 text-green-600" />
      case 'comparison':
        return <BarChart3 className="w-5 h-5 text-purple-600" />
      case 'clarification':
        return <HelpCircle className="w-5 h-5 text-yellow-600" />
      case 'error':
        return <AlertCircle className="w-5 h-5 text-red-600" />
      default:
        return <Lightbulb className="w-5 h-5 text-gray-600" />
    }
  }

  const getTypeColor = () => {
    switch (type) {
      case 'answer':
        return 'bg-blue-50'
      case 'filter':
        return 'bg-green-50'
      case 'comparison':
        return 'bg-purple-50'
      case 'clarification':
        return 'bg-yellow-50'
      case 'error':
        return 'bg-red-50'
      default:
        return 'bg-gray-50'
    }
  }

  // Format text content, handling objects and structured data
  const formatTextContent = content => {
    if (typeof content !== 'string') {
      // If it's an object or array, format it nicely
      if (typeof content === 'object' && content !== null) {
        return JSON.stringify(content, null, 2)
      }
      return String(content)
    }

    // Check if the string contains object patterns that weren't stringified
    if (content.includes('[object Object]')) {
      // Try to parse and reformat any embedded JSON
      try {
        const parsed = JSON.parse(content)
        return JSON.stringify(parsed, null, 2)
      } catch {
        // If parsing fails, return as-is (it's just text with [object Object])
        return content
      }
    }

    return content
  }

  return (
    <div className="p-6 space-y-4">
      {/* Cache Badge */}
      {cached && (
        <div className="flex items-center justify-between gap-3">
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium border border-blue-200">
            <span className="text-base">⚡</span>
            <span>Instant result (cached)</span>
          </div>

          {/* Share Link Button */}
          {cacheKey && (
            <button
              onClick={handleCopyShareLink}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200"
              title="Copy shareable link"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 text-green-600" />
                  <span className="text-green-600">Copied!</span>
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  <span>Share</span>
                </>
              )}
            </button>
          )}
        </div>
      )}

      {/* Response Header */}
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg ${getTypeColor()}`}>
          {getTypeIcon()}
        </div>
        <div className="flex-1">
          <p className="text-gray-800 leading-relaxed whitespace-pre-wrap text-base">
            {formatTextContent(text)}
          </p>
        </div>
      </div>

      {/* Insights (for comparison type) */}
      {insights && (
        <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
          <div className="flex items-start gap-2">
            <Lightbulb className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-purple-900 mb-1">
                Key Insights
              </p>
              <p className="text-sm text-purple-800">{insights}</p>
            </div>
          </div>
        </div>
      )}

      {/* Suggestions (for clarification type) */}
      {suggestions && suggestions.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700">Try asking:</p>
          <div className="space-y-1.5">
            {suggestions.map((suggestion, index) => (
              <div
                key={index}
                className="px-3 py-2 bg-gray-50 rounded-lg text-sm text-gray-700 border border-gray-200"
              >
                • {suggestion}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Metadata from cached queries */}
      {metadata && (
        <div className="pt-3 border-t border-gray-100 text-xs text-gray-600 space-y-1">
          <div className="flex items-center justify-between">
            <span>
              <span className="font-medium">Original Query:</span>{' '}
              {metadata.prompt}
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span>
              <span className="font-medium">Category:</span> {metadata.category}
            </span>
            <span>
              <span className="font-medium">Usage Count:</span>{' '}
              {metadata.usageCount}
            </span>
            <span>
              <span className="font-medium">Last Accessed:</span>{' '}
              {new Date(metadata.lastAccessed).toLocaleString()}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

AiTextResult.propTypes = {
  type: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  cached: PropTypes.bool,
  timestamp: PropTypes.string,
  cacheKey: PropTypes.string,
  metadata: PropTypes.object,
  suggestions: PropTypes.arrayOf(PropTypes.string),
  insights: PropTypes.string,
}
